import './studyReadingMobile.css';
import { StudyTranscriptPanel } from './StudyTranscriptPanel';
import { StudyImageAnnotations } from './StudyImageAnnotations';
import { BookOpen, ExternalLink, FileText, Sparkles, ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { getKidSub, useAuthStore } from '@/auth/authStore';

import { StudyPdfAnnotationViewer } from './StudyPdfAnnotationViewer';
import { getStudyDocument, type StudyDocument, type StudyTerm } from './studyApi';
import { StudyMaterialAnalysisStrip } from './StudyMaterialAnalysisStrip';
import { type MaterialCardAction, StudyMaterialCard } from './StudyMaterialCard';
import { StudyMaterialNotes } from './StudyMaterialNotes';
import { materialTitle } from './studyMaterialRow';

/** Subject Space Materials tab and material preview (split out of StudySubjectPage, SIR-HYG-07). */

export function SubjectMaterials({
  materials,
  subjects,
  terms,
  termLabel,
  historical,
  movingId,
  onCapture,
  onOpen,
  onMove,
  onMoveTerm,
  onUndoAuto,
  onDelete,
  deletingId,
}: {
  materials: StudyDocument[];
  subjects: Array<{ id: string; subject_key: string; display_name: string }>;
  terms: StudyTerm[];
  termLabel: string;
  historical: boolean;
  movingId?: string;
  onCapture: () => void;
  onOpen: (documentId: string) => void;
  onMove: (documentId: string, subjectKey: string) => void;
  onMoveTerm: (documentId: string, subjectKey: string, termId: string) => void;
  onUndoAuto: (documentId: string) => void;
  onDelete: (documentId: string) => void;
  deletingId?: string;
}) {
  if (materials.length === 0) {
    return (
      <div className="mt-8 rounded-3xl border-2 border-dashed border-hairline bg-canvas-pure p-8 text-center">
        <BookOpen className="mx-auto h-8 w-8 text-brand-sky" aria-hidden="true" />
        <h2 className="mt-4 text-xl font-extrabold text-ink">No materials found {termLabel}</h2>
        <p className="mt-2 font-medium text-slate2">Try another Term or name.</p>
        {!historical && (
          <button type="button" onClick={onCapture} className="btn-pill-primary mt-5">
            Add schoolwork
          </button>
        )}
      </div>
    );
  }
  return (
    <section className="mt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            Source library
          </p>
          <h2 className="mt-1 text-2xl font-extrabold text-ink">Materials {termLabel}</h2>
        </div>
        <p className="text-sm font-bold text-slate2">Showing {materials.length} items</p>
      </div>
      <div className="mt-5 rounded-3xl border border-hairline bg-canvas-pure shadow-card-soft">
        {materials.map((material, index) => {
          const title = materialTitle(material);
          const actions: MaterialCardAction[] = [];
          if (!historical) {
            actions.push({
              id: 'move',
              label: 'Move to another subject',
              dialog: {
                title: `Move ${title}`,
                body: (close) => (
                  <label className="block text-sm font-extrabold text-ink">
                    Subject
                    <select
                      aria-label={`Organise ${material.file_name}`}
                      data-autofocus
                      value={material.subject?.subject_key ?? ''}
                      disabled={movingId === material.id}
                      onChange={(event) => {
                        onMove(material.id, event.target.value);
                        close();
                      }}
                      className="mt-1 block h-11 w-full rounded-xl border border-hairline bg-canvas px-3 text-sm font-bold text-ink outline-none focus:border-brand-sky"
                    >
                      <option value="">Organise later</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.subject_key}>
                          {subject.display_name}
                        </option>
                      ))}
                    </select>
                  </label>
                ),
              },
            });
          }
          if (terms.length > 1 && material.term_id && material.subject) {
            const assigned = material.subject;
            actions.push({
              id: 'term',
              label: 'File in another Term',
              dialog: {
                title: `File ${title} in another Term`,
                body: (close) => (
                  <label className="block text-sm font-extrabold text-ink">
                    Term
                    <select
                      aria-label={`File ${material.file_name} in Term`}
                      data-autofocus
                      value={material.term_id ?? ''}
                      disabled={movingId === material.id}
                      onChange={(event) => {
                        onMoveTerm(material.id, assigned.subject_key, event.target.value);
                        close();
                      }}
                      className="mt-1 block h-11 w-full rounded-xl border border-hairline bg-canvas px-3 text-sm font-bold text-ink outline-none focus:border-brand-sky"
                    >
                      {terms
                        .filter((term) => term.status !== 'archived')
                        .map((term) => {
                          const available =
                            term.id === material.term_id ||
                            term.subjects?.some(
                              (candidate) => candidate.subject_key === assigned.subject_key,
                            );
                          return (
                            <option key={term.id} value={term.id} disabled={!available}>
                              {term.label}
                              {!available ? ' · subject not in this Term' : ''}
                            </option>
                          );
                        })}
                    </select>
                  </label>
                ),
              },
            });
          }
          if (material.classification_state === 'auto_organised') {
            actions.push({
              id: 'undo-auto',
              label: 'Undo automatic filing',
              onSelect: () => onUndoAuto(material.id),
            });
          }
          actions.push({
            id: 'delete',
            label: 'Delete material',
            danger: true,
            dialog: {
              title: `Delete ${title}?`,
              body: (close) => (
                <div role="group" aria-label={`Delete ${material.file_name}`}>
                  <p className="text-sm font-medium text-ink-soft">
                    This also removes linked Tutor sessions and notes.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="btn-pill-primary disabled:opacity-50"
                      disabled={deletingId === material.id}
                      onClick={() => {
                        onDelete(material.id);
                        close();
                      }}
                    >
                      Confirm delete
                    </button>
                    <button type="button" className="btn-pill-secondary" onClick={close}>
                      Cancel
                    </button>
                  </div>
                </div>
              ),
            },
          });
          return (
            <div key={material.id} className={index > 0 ? 'border-t border-hairline' : undefined}>
              <StudyMaterialCard
                document={material}
                variant="row"
                actions={actions}
                onOpen={() => onOpen(material.id)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function MaterialReader({
  document,
  title,
  loading,
  error,
  starting,
  startError,
  onStudy,
  onUnsavedChange,
  onClose,
  onRetry,
}: {
  document?: Awaited<ReturnType<typeof getStudyDocument>>;
  /** The card title (chosen name > analysed topic > file name), known before the read returns. */
  title?: string;
  loading: boolean;
  error: boolean;
  starting: boolean;
  startError?: string;
  onStudy: () => void;
  onUnsavedChange: (unsaved: boolean) => void;
  onClose: () => void;
  onRetry?: () => void;
}) {
  const [dirty, setDirty] = useState(false);
  const [recordsDirty, setRecordsDirty] = useState(false);
  const [imageDirty, setImageDirty] = useState(false);
  const [transcriptDirty, setTranscriptDirty] = useState(false);
  useEffect(
    () => setDirty(recordsDirty || imageDirty || transcriptDirty),
    [recordsDirty, imageDirty, transcriptDirty],
  );
  const [tutorIntent, setTutorIntent] = useState(false);
  const [pdfTutorRequest, setPdfTutorRequest] = useState(0);
  const [pdfTutorOpen, setPdfTutorOpen] = useState(false);
  const approvedTutor = useRef(false);
  const learner = useAuthStore(() => getKidSub());
  const [openedBy] = useState(learner);
  const changedLearner = learner !== openedBy;
  useEffect(() => {
    if (changedLearner) onUnsavedChange(false);
  }, [changedLearner, onUnsavedChange]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !changedLearner &&
      dirty &&
      !(approvedTutor.current && /^\/learn\/study\/sessions\/[^/]+$/.test(nextLocation.pathname)) &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  useBeforeUnload((event) => {
    if (!changedLearner && dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    onUnsavedChange(!changedLearner && dirty);
  }, [changedLearner, dirty, onUnsavedChange]);
  useEffect(() => {
    if (startError) approvedTutor.current = false;
  }, [startError]);
  function study() {
    approvedTutor.current = true;
    setTutorIntent(false);
    onStudy();
  }
  if (changedLearner)
    return (
      <div
        role="alertdialog"
        aria-label="Student session changed"
        className="flex h-full items-center justify-center bg-canvas-pure p-5"
      >
        <div className="rounded-3xl bg-canvas-pure p-6 text-ink">
          <p>The student session changed. Reopen your own material to continue.</p>
          <button className="btn-pill-secondary mt-4" onClick={onClose}>
            Back to Study
          </button>
        </div>
      </div>
    );
  return (
    <section
      role="region"
      aria-labelledby="study-material-title"
      className={`study-material-reader flex h-full w-full flex-col overflow-hidden bg-canvas-pure ${document?.mime_type === 'application/pdf' && pdfTutorOpen ? 'md:pr-[420px]' : ''}`}
    >
      <header
        className={`relative z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-hairline bg-canvas-pure px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))] sm:px-7 ${document?.mime_type === 'application/pdf' ? 'max-md:hidden' : ''}`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Back to Study"
          className="btn-pill-secondary shrink-0 px-4 sm:px-6"
        >
          <ArrowLeft className="mr-2 inline h-4 w-4" aria-hidden="true" />
          <span>
            Back<span className="hidden sm:inline"> to Study</span>
          </span>
        </button>
        <div className="min-w-0 flex-1 basis-40">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            Source material
          </p>
          <h2 id="study-material-title" className="mt-1 truncate text-xl font-extrabold text-ink">
            {title ??
              (document
                ? materialTitle(document)
                : error
                  ? 'Material unavailable'
                  : 'Opening material…')}
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {document && (
            <button
              type="button"
              onClick={() => {
                if (document.mime_type === 'application/pdf')
                  setPdfTutorRequest((current) => current + 1);
                else if (dirty) setTutorIntent(true);
                else study();
              }}
              disabled={starting}
              className="btn-pill-primary"
            >
              <Sparkles className="mr-2 inline h-4 w-4" aria-hidden="true" />
              {starting ? 'Starting…' : 'Study with AI Tutor'}
            </button>
          )}
        </div>
      </header>
      {(blocker.state === 'blocked' || tutorIntent) && (
        <div
          role="alertdialog"
          aria-label="Unsaved records"
          className="border-b border-hairline bg-wash-sunshine px-5 py-4 text-sm text-ink max-md:fixed max-md:inset-x-3 max-md:top-[max(1rem,env(safe-area-inset-top))] max-md:z-[150] max-md:rounded-2xl max-md:shadow-floating-chrome"
        >
          <p>Your records or annotations have not been saved.</p>
          <button
            className="btn-pill-secondary mt-3"
            onClick={() => {
              if (blocker.state === 'blocked') blocker.reset();
              setTutorIntent(false);
            }}
          >
            Keep writing
          </button>
          <button
            className="btn-pill-secondary ml-3 mt-3"
            onClick={() => {
              if (blocker.state === 'blocked') blocker.proceed();
              else study();
            }}
          >
            {tutorIntent ? 'Discard draft and start Tutor' : 'Discard draft and leave'}
          </button>
        </div>
      )}
      {startError && (
        <p
          role="alert"
          className="border-b border-hairline bg-wash-coral px-5 py-3 text-sm font-bold text-ink sm:px-7"
        >
          {startError}
        </p>
      )}
      {document && (
        <div className={document.mime_type === 'application/pdf' ? 'max-md:hidden' : undefined}>
          <StudyMaterialAnalysisStrip document={document} />
        </div>
      )}
      <div
        className={`min-h-0 min-w-0 flex-1 overflow-y-auto bg-surface ${starting ? 'pointer-events-none' : ''} ${document?.mime_type.startsWith('image/') ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:overflow-hidden' : 'p-4 sm:p-6'}`}
      >
        <fieldset disabled={starting} aria-busy={starting} className="contents">
          {loading && <p className="font-bold text-slate2">Preparing a private preview…</p>}
          {error && (
            <div role="alert" className="rounded-2xl bg-wash-coral p-5 font-bold text-ink">
              This material could not be opened. Return to Study or try again.
              {onRetry && (
                <button className="btn-pill-secondary ml-3" onClick={onRetry}>
                  Retry material
                </button>
              )}
            </div>
          )}
          {!loading && !error && document?.mime_type.startsWith('image/') && (
            <>
              <StudyImageAnnotations document={document} onUnsavedChange={setImageDirty} />
              <aside className="min-h-0 border-t border-hairline bg-canvas-pure pb-[env(safe-area-inset-bottom)] lg:overflow-y-auto lg:border-l lg:border-t-0">
                <StudyMaterialNotes document={document} onUnsavedChange={setRecordsDirty} />
                <StudyTranscriptPanel
                  documentId={document.id}
                  hasSubject={Boolean(document.subject)}
                  onUnsavedChange={setTranscriptDirty}
                />
              </aside>
            </>
          )}
          {!loading && !error && document?.mime_type === 'application/pdf' && (
            <StudyPdfAnnotationViewer
              documentId={document.id}
              readUrl={document.read_url}
              fileName={document.file_name}
              sourceRevision={document.source_revision}
              mobileLearningAction={<StudyMaterialAnalysisStrip document={document} />}
              onUnsavedChange={setDirty}
              tutorRequest={pdfTutorRequest}
              onTutorOpenChange={setPdfTutorOpen}
              onBack={onClose}
              key={document.id}
            />
          )}
          {!loading &&
            !error &&
            document &&
            document.mime_type !== 'application/pdf' &&
            !document.mime_type.startsWith('image/') && (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <FileText className="h-12 w-12 text-brand-sky" aria-hidden="true" />
                <p className="mt-4 max-w-md font-medium text-slate2">
                  PowerPoint files open in a new tab. Your original remains private and the link
                  expires shortly.
                </p>
                <a
                  href={document.read_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-pill-primary mt-5"
                >
                  Open file
                  <ExternalLink className="ml-2 inline h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            )}
        </fieldset>
      </div>
    </section>
  );
}
